import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { rootRoute, requireAuth } from './__root'
import { useApprovals, useApprovalDetail, useDecideRenovate, useVisibilityRefetch } from '../api/hooks'
import { ApiRequestError } from '../api/client'
import { ApprovalCard } from '../components/ApprovalCard.js'
import { useState } from 'react'

export const approvalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/approvals',
  beforeLoad: requireAuth,
  component: ApprovalsList,
})

export const approvalDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/approvals/$id',
  beforeLoad: requireAuth,
  component: ApprovalDetail,
})

function StateBadge({ state }: { state: string }) {
  const colour =
    state === 'awaiting_approval'
      ? 'bg-yellow-900/50 text-yellow-300'
      : state === 'approved'
        ? 'bg-green-900/50 text-green-300'
        : state === 'rejected'
          ? 'bg-red-900/50 text-red-300'
          : 'bg-gray-800 text-gray-400'
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-medium ${colour}`}>
      {state.replace(/_/g, ' ')}
    </span>
  )
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString()
}

function resolvedBadgeColour(state: string): string {
  if (state === 'completed' || state === 'approved') return 'bg-green-900/50 text-green-300'
  if (state === 'rejected' || state === 'failed') return 'bg-red-900/50 text-red-300'
  return 'bg-gray-800 text-gray-400'
}

function ApprovalsList() {
  const { data, isLoading, error } = useApprovals()
  useVisibilityRefetch(['approvals'])

  if (isLoading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Approvals</h1>
        <p className="text-gray-400">Loading...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Approvals</h1>
        <p className="text-red-400">Failed to load: {error.message}</p>
      </div>
    )
  }

  const pending = data?.pending ?? []
  const resolved = data?.resolved ?? []

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold mb-4">Approvals</h1>

      {/* Pending cards */}
      {pending.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center">
          <p className="text-gray-500">Nothing awaiting approval</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pending.map((a) => (
            <ApprovalCard approval={a as any} key={a.id} />
          ))}
        </div>
      )}

      {/* Resolved tail */}
      {resolved.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Resolved (last 6h)
          </h2>
          <div className="space-y-3 opacity-70">
            {resolved.map((a) => (
              <ApprovalCard approval={a as any} key={a.id} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function ApprovalDetail() {
  const { id } = approvalDetailRoute.useParams()
  const { data, isLoading, error } = useApprovalDetail(id)
  const decide = useDecideRenovate()
  const navigate = useNavigate()
  const [inlineError, setInlineError] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Approval</h1>
        <p className="text-gray-400">Loading...</p>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Approval</h1>
        <p className="text-gray-400 mb-4">
          Approval not found — may already be resolved.
        </p>
        <Link to="/approvals" className="text-blue-400 hover:text-blue-300 text-sm transition-colors">
          ← Back to list
        </Link>
      </div>
    )
  }

  const handleDecide = (decision: 'approve' | 'reject') => {
    setInlineError(null)
    decide.mutate(
      { id, decision },
      {
        onSuccess: () => {
          navigate({ to: '/approvals' })
        },
        onError: (err) => {
          if (err instanceof ApiRequestError && err.status === 409) {
            setInlineError('Already resolved by Telegram')
          } else {
            setInlineError('Error — try again')
          }
        },
      },
    )
  }

  return (
    <div className="p-6 max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/approvals" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">
          ← Approvals
        </Link>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-100">
            {data.mr_title ?? `MR #${data.id}`}
          </h1>
          <StateBadge state={data.state} />
        </div>

        <div className="space-y-1 text-sm">
          <div className="flex gap-2">
            <span className="text-gray-500 w-24 shrink-0">MR URL</span>
            <a
              href={data.mr_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 transition-colors break-all"
            >
              {data.mr_url}
            </a>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-500 w-24 shrink-0">Project ID</span>
            <span className="text-gray-300">{data.project_id}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-500 w-24 shrink-0">Created</span>
            <span className="text-gray-300">{formatDate(data.created_at)}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-gray-500 w-24 shrink-0">Updated</span>
            <span className="text-gray-300">{formatDate(data.updated_at)}</span>
          </div>
        </div>

        {data.assess_summary && (
          <div className="space-y-1">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Summary</h2>
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{data.assess_summary}</p>
          </div>
        )}

        {data.assess_findings && (
          <div className="space-y-1">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Findings</h2>
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{data.assess_findings}</p>
          </div>
        )}
      </div>

      {data.state === 'awaiting_approval' && (
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleDecide('approve')}
            disabled={decide.isPending}
            className="px-4 py-2 text-sm bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white rounded transition-colors"
          >
            Approve
          </button>
          <button
            onClick={() => handleDecide('reject')}
            disabled={decide.isPending}
            className="px-4 py-2 text-sm bg-red-800 hover:bg-red-700 disabled:opacity-50 text-white rounded transition-colors"
          >
            Reject
          </button>
          {inlineError && (
            <span className="text-sm text-red-400">{inlineError}</span>
          )}
        </div>
      )}
    </div>
  )
}
